import type { BandRole } from '../api';
import {
  PERMISSIONS,
  type Permission,
  normalizePermissions,
  isLayoutPermission,
} from './permissions';

/**
 * Frontend gate of the 3 PDF layout editors (rider / daysheet / tourbook).
 *
 * HISTORY: this started (option B, 2026-09-24) as the exact clone of the
 * `finance_access` pattern, with a single `layout_access` flag per member.
 * Chantier C (2026-09-25) replaced that boolean with the fine-grained matrix
 * (`src/lib/permissions.ts`): a member is now granted each layout separately
 * (`layout.rider`, `layout.daysheet`, `layout.tourbook`, `layout.presets`).
 * This module is kept as the ONE place that answers "may I edit a layout?" so
 * the sidebar, the page guards and the editors cannot drift apart — it now
 * delegates the decision to the matrix (`can()`), which is the same rule the
 * server enforces (`canWriteLayout(req, bandId, which)` in server/authz.js).
 *
 * `layout_access` (the legacy flag) is still honoured when a payload carries no
 * matrix — NON-REGRESSION: production shipped with `layout_access = 0`
 * everywhere, and the API keeps returning both flags as derived values.
 */

export type LayoutScope = 'rider' | 'daysheet' | 'tourbook';

export interface LayoutAccessInput {
  /** `user.global_role === 'superadmin'` — implicit full access on every band. */
  isSuperAdmin?: boolean;
  /** Read-only showcase (demo) account: it opens every page, and never saves. */
  isShowcase?: boolean;
  /** The caller's role on the ACTIVE band (a superadmin maps to 'owner'). */
  bandRole?: BandRole | null;
  /**
   * The caller's EFFECTIVE capability matrix on the active band
   * (`BandProfile.permissions`). When present it is authoritative.
   */
  permissions?: string[] | null;
  /** LEGACY fallback: raw `user_bands.layout_access` flag (0/1). */
  layoutAccess?: number | boolean | null;
}

/**
 * The capability that opens one layout scope — the mirror of the server's
 * `canWriteLayout(req, bandId, which)`.
 */
export function layoutPermissionFor(scope: LayoutScope): Permission {
  return `layout.${scope}` as Permission;
}

/** The layout capabilities of a payload (matrix first, legacy flag otherwise). */
function grantedLayoutPermissions(input: LayoutAccessInput): Permission[] {
  if (Array.isArray(input.permissions)) return normalizePermissions(input.permissions);
  // Legacy payload: the single flag opened all 3 editors.
  const legacy = input.layoutAccess === 1 || input.layoutAccess === true;
  return legacy ? PERMISSIONS.filter(isLayoutPermission) : [];
}

/** superadmin || showcase || band owner — the implicit "all capabilities" set. */
function hasImplicitFullAccess(input: LayoutAccessInput): boolean {
  return !!input.isSuperAdmin || !!input.isShowcase || input.bandRole === 'owner';
}

/**
 * Can the signed-in user edit at least ONE PDF layout of this band?
 *   superadmin || showcase || band owner || any granted `layout.*`
 * Drives the sidebar « Layout editor » section (the brief: shown as soon as
 * one `layout.*` capability is held) — the per-route guards and the editors use
 * the finer `canEditLayoutFor` below.
 */
export function canEditLayoutsFor(input: LayoutAccessInput): boolean {
  if (hasImplicitFullAccess(input)) return true;
  return grantedLayoutPermissions(input).some(isLayoutPermission);
}

/**
 * Can the signed-in user edit THAT layout? Used by the page guard of each
 * layout route and by the editor itself (gate + autosave + save): a member
 * granted only `layout.daysheet` never opens, and never writes, the rider
 * layout.
 */
export function canEditLayoutFor(input: LayoutAccessInput, scope: LayoutScope): boolean {
  if (hasImplicitFullAccess(input)) return true;
  return grantedLayoutPermissions(input).includes(layoutPermissionFor(scope));
}

/**
 * The GLOBAL (platform-wide) template is untouched by any granted capability:
 * it affects every band, so it stays admin/superadmin only — server side AND in
 * the UI (the "Global template" tab is hidden for anybody else).
 */
export function canEditGlobalLayout(isSuperAdmin?: boolean): boolean {
  return !!isSuperAdmin;
}